import { timeBucket, VisitAggregateSchema, type Device, type Tier, type VisitAggregate, type VisitDish } from "@arfood/shared";

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
const AGGREGATE_VERSION = 1;

// Stufe B: Der Client zählt im Arbeitsspeicher und sendet EIN ID-loses Aggregat —
// beim Verlassen der Seite bzw. bei der Bestellabsicht. Es gibt bewusst KEINE
// Sitzungs-Kennung mehr: eine im Client erzeugte und an den Server gesendete Kennung
// fällt nach EDSA-Leitlinien 2/2023 Rn 52–53 unter Art. 5(3) ePrivacy / § 25 TDDDG,
// auch wenn sie nur im RAM existiert. Herleitung: ANALYTICS-PLAN.md §7.
//
// Der Feldkatalog ist eingefroren — neue Dimensionen sind Rechtsentscheidungen.

export interface AnalyticsContext {
  venue: string;
  loc?: string | undefined;
  device: Device;
  getLang: () => string | undefined;
  getTier: () => Tier;
}

type Counter = Omit<VisitDish, "dish">;
const emptyCounter = (): Counter => ({
  views: 0, detailOpens: 0, arEnters: 0, arDwellMs: 0, cartAdds: 0, cartRemoves: 0, intentQty: 0,
});

let ctx: AnalyticsContext | null = null;
let startedAt = 0;
let sent = false;
const dishes = new Map<string, Counter>();
const cart = new Set<string>();
let fallbacks = 0;
let arUnsupported = 0;

function counter(dish: string): Counter {
  let c = dishes.get(dish);
  if (!c) dishes.set(dish, (c = emptyCounter()));
  return c;
}

export function initAnalytics(c: AnalyticsContext): void {
  ctx = c;
  startedAt = Date.now();
}

// --- Zähler (ersetzen die früheren Einzel-Events) ---
export const count = {
  view(dish: string): void {
    counter(dish).views++;
  },
  detailOpen(dish: string): void {
    counter(dish).detailOpens++;
  },
  arEnter(dish: string): void {
    counter(dish).arEnters++;
  },
  arDwell(dish: string, ms: number): void {
    counter(dish).arDwellMs += Math.max(0, Math.round(ms));
  },
  cartAdd(dish: string): void {
    counter(dish).cartAdds++;
  },
  cartRemove(dish: string): void {
    counter(dish).cartRemoves++;
  },
  fallback(): void {
    fallbacks++;
  },
  arUnsupported(): void {
    arUnsupported++;
  },
  // Bestellabsicht: Mengen merken, Warenkorb für die Affinität festhalten — und sofort
  // senden, damit die Absicht nicht verloren geht, wenn die Seite offen bleibt.
  intent(items: { dish: string; qty: number }[]): void {
    for (const it of items) {
      counter(it.dish).intentQty += it.qty;
      cart.add(it.dish);
    }
    flush();
  },
};

function build(): VisitAggregate | null {
  if (!ctx) return null;
  const { day, dayOfWeek, bucket } = timeBucket(new Date());
  const candidate = {
    v: AGGREGATE_VERSION,
    venue: ctx.venue,
    loc: ctx.loc,
    day,
    dayOfWeek,
    bucket,
    lang: ctx.getLang(),
    device: ctx.device,
    tier: ctx.getTier(),
    durationMs: Math.max(0, Date.now() - startedAt),
    fallbacks,
    arUnsupported,
    dishes: [...dishes.entries()].map(([dish, c]) => ({ dish, ...c })),
    cart: [...cart],
  };
  const parsed = VisitAggregateSchema.safeParse(candidate);
  if (!parsed.success) {
    console.warn("[analytics] ungültiges Aggregat verworfen:", parsed.error.issues);
    return null;
  }
  return parsed.data;
}

// Genau einmal senden. Ein zweiter Aufruf (z. B. Bestellabsicht und danach pagehide)
// würde denselben Besuch doppelt zählen.
export function flush(): void {
  if (sent) return;
  const aggregate = build();
  if (!aggregate) return;
  sent = true;

  const body = JSON.stringify(aggregate);
  if (!API_BASE) {
    console.debug("[analytics] Aggregat (kein API_BASE gesetzt)", aggregate);
    return;
  }
  // text/plain ist CORS-safelisted → cross-origin ohne Preflight (den sendBeacon nicht
  // kann). Der Server parst den Body content-type-unabhängig als JSON.
  const url = `${API_BASE}/a`;
  const ok = navigator.sendBeacon(url, new Blob([body], { type: "text/plain" }));
  if (!ok) {
    void fetch(url, { method: "POST", body, keepalive: true }).catch(() => {});
  }
}
